<template>
	<section class="poi-popup">
		<section class="poi-popup__content">
			<div class="poi-popup__content__header">
				<BoxHeaderBackground slug="poi-popup__content__header" />
				<div class="poi-popup__content__header__text">
					{{ tile?.name }}
				</div>
				<div class="poi-popup__content__header__subtext">
					{{ tile?.description }}
				</div>
				<div class="poi-popup__content__header__subtext">
					{{ translate("ui.monument_reward_achieved") }}
				</div>
			</div>
			<div class="poi-detail-wrapper">
				<div class="poi-detail">
					<div class="poi-detail__content">
						<BidirectionalTextLabel
							:text="translate('ui.rewards')"
							:theme="LabelThemes.GREEN"
							class="rewards-label"
						/>
						<div class="poi-detail-reward">
							<Tooltip
								v-for="(
									amount, currency, index
								) in tile?.getRewards()"
								:key="currency"
								:placement="Placements.TOP"
							>
								<template #element>
									<AppPrice
										:key="index"
										:amount="amount || 0"
										:currency="currency"
										:resourceCheck="false"
										:iconSize="16"
									/>
								</template>
								<template #tooltip>
									<span>
										{{
											translate(
												`resource.${resourceTypeToCurrencyType(
													currency,
												)}`,
											)
										}}
									</span>
								</template>
							</Tooltip>
						</div>
						<div
							class="reward-text"
							v-for="text in rewardText"
							:key="text"
						>
							{{ text }}
						</div>
						<div class="poi-detail-reward">
							<Tooltip
								v-for="(resource, index) in tile?.storage"
								:key="resource[0]"
								:placement="Placements.TOP"
							>
								<template #element>
									<AppPrice
										:key="index"
										:amount="resource[1]"
										:currency="resource[0]"
										:resourceCheck="false"
										:iconSize="16"
									/>
								</template>
								<template #tooltip>
									<span>
										{{
											translate(
												`resource.${resourceTypeToCurrencyType(
													resource[0],
												)}`,
											)
										}}
									</span>
								</template>
							</Tooltip>
						</div>
					</div>
					<RectangleBoxContainer />
				</div>
			</div>
		</section>
		<div class="poi-popup__buttons">
			<AppTextButton
				class="poi-popup__btn"
				:type="StatusTypes.SUCCESS"
				:text="translate('button.claim').toUpperCase()"
				@click="claimRewards"
			/>
		</div>
	</section>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import BidirectionalTextLabel from "@/components/BidirectionalTextLabel.vue";
import BoxHeaderBackground from "@/components/SVG/BoxHeaderBackground.vue";
import RectangleBoxContainer from "@/components/SVG/RectangleBoxContainer.vue";
import Tooltip from "@/components/Tooltip.vue";
import type AbstractPoiTileEntity from "@/game/entities/poi/AbstractPoiTileEntity";
import useRuntimeStore from "@/store/useRuntimeStore";
import { LabelThemes, Placements, StatusTypes } from "@/types/ui";
import { resourceTypeToCurrencyType } from "@/utils/formatPrice";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { canTranslate, translate } from "@/utils/translate";
import { computed } from "vue";

const { tile } = defineProps<{
	tile: AbstractPoiTileEntity | null;
}>();

const emit = defineEmits(["closePopup"]);

const { setPoiJustRevealed } = useRuntimeStore();

const rewardText = computed((): Array<string> => {
	if (!tile) return [];
	const text = [] as Array<string>;
	for (let i = 0; i < tile.rewardText.length; i++) {
		const rewardText = tile.rewardText[i];
		text[i] = canTranslate(rewardText) ? translate(rewardText) : "";
		Object.entries(tile.rewardTextReplace[i] ?? {}).forEach(
			([key, value]: [string, string]): void => {
				const textTranslated = canTranslate(value)
					? translate(value)
					: value;
				text[i] = replacePlaceholder(text[i], key, textTranslated);
			},
		);
	}
	return text;
});

const claimRewards = (): void => {
	if (!tile) return;
	setPoiJustRevealed(false);
	emit("closePopup");
};
</script>

<style lang="scss" scoped>
.poi-popup {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 1rem;
	height: 100%;
	color: #f2dcb5;

	&__content {
		@include flex-col-start;
		position: relative;
		box-shadow: inset 0 0 1.875rem 0 rgba(29, 30, 33, 0.8);
		border-width: 0.125rem;
		border-style: solid;
		border-image-source: linear-gradient(
			to top,
			rgb(131, 110, 73),
			rgb(191, 163, 112)
		);
		border-image-slice: 1;
		background-image: linear-gradient(
			to top,
			rgb(171, 136, 73),
			rgb(242, 220, 181)
		);
		width: 100%;
		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 0.6;
			mix-blend-mode: color-burn;
			background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
				repeat center;
			background-size: 40%;
			width: 100%;
			height: 100%;
			pointer-events: none;
			content: "";
		}
		.poi-detail-wrapper {
			width: 100%;
			@include flex-row-center;
		}
		.poi-detail {
			width: calc(100% - 2rem);
			@include flex-row-center;
			position: relative;
			margin: 1rem 0;
			box-shadow: inset 0 0 2.375rem 0rem
				rgba($color: #2bad50, $alpha: 0.7);
			background-image: linear-gradient(
				to top,
				rgb(37, 38, 42),
				rgb(66, 67, 74)
			);

			&::before {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 0.6;
				mix-blend-mode: color-burn;
				background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
					repeat center;
				background-size: 40%;
				width: 100%;
				pointer-events: none;
				content: "";
			}

			&__content {
				@include flex-col-center;
				padding: 2rem 0.5rem;
				width: 100%;
				height: 100%;
			}

			.rewards-label {
				position: absolute;
				top: -1rem;
				z-index: 1;
				width: auto;
				height: 2rem;
			}

			:deep(.bidirectional-text-label) {
				filter: drop-shadow(0 0 0.25rem rgba(0, 0, 0, 0.6));
			}

			:deep(.price__value) {
				color: #f2dcb5 !important;
			}

			.poi-detail-reward {
				display: flex;
				flex-wrap: wrap;
				justify-content: center;
				gap: 0.625rem;

				.price {
					min-width: 34%; // max 2 na riadok
				}
			}
			.reward-text {
				font-size: 0.875rem;
				text-align: center;
			}
		}

		&__header {
			padding-bottom: 1rem;
			width: 100%;
			@include flex-col-center;
			position: relative;
			padding: 1rem;
			color: rgb(55, 38, 21);

			&__text {
				margin-bottom: 0.25rem;
				font-weight: 800;
				text-align: center;
				text-transform: uppercase;
			}
			&__subtext {
				text-align: center;
				&:not(:last-child) {
					margin-bottom: 1rem;
				}
			}

			:deep(.box-header-bg) {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 0.6;
				z-index: 0;
				mix-blend-mode: multiply;
				width: 100%;
				height: 100%;
			}

			:deep(.solid-bg-polygon) {
				fill: rgba(145, 113, 56, 0.8) !important;
			}

			:deep(.texture-bg-polygon) {
				opacity: 0.4;
			}
		}
	}
	&__buttons {
		margin-top: 1rem;
	}
}
</style>
